/**
 * Shared helpers used across admin panels: date formatters, image upload field,
 * and generic modal shell.
 */
import { useRef, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";


export function formatDateTime(iso) {
  try { return new Date(iso).toLocaleString("en-ZA"); } catch { return iso; }
}

export function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}


export function Modal({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,43,65,0.45)" }} onClick={onClose}>
      <div
        className={`p-6 md:p-8 w-full ${wide ? "max-w-3xl" : "max-w-lg"}`}
        style={{
          backgroundColor: "var(--ser-surface)",
          border: "1px solid var(--ser-border)",
          borderRadius: "2px",
          maxHeight: "90vh",
          overflowY: "auto",
          overflowX: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <h3 className="font-serif-display text-xl" style={{ color: "var(--ser-primary-dark)" }}>{title}</h3>
          <button onClick={onClose} className="p-1 hover:opacity-70" data-testid="modal-close">
            <X className="w-5 h-5" style={{ color: "var(--ser-text-muted)" }} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}


export function ImageField({ label, value, onChange, testIdPrefix }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image is larger than 8 MB. Please compress it.");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <label className="ser-label">{label}</label>
      <div className="flex items-center gap-3 mb-2">
        <input
          className="ser-input flex-1"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste image URL, or upload from your device →"
          data-testid={`${testIdPrefix}-url`}
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={onFile}
          className="hidden"
          data-testid={`${testIdPrefix}-file`}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="btn-ser-outline whitespace-nowrap"
          data-testid={`${testIdPrefix}-upload-btn`}
        >
          {uploading ? "Uploading…" : "Upload"}
        </button>
      </div>
      {value && (
        <img src={value} alt="preview" className="w-full h-32 object-cover rounded"
          style={{ border: "1px solid var(--ser-border, #e5e5e5)" }} />
      )}
    </div>
  );
}
